import { AvatarChip } from "components/designV2/avatarChip/avatarChip";
import { Spacing } from "components/designV2/constants";
import { ListRow } from "components/designV2/listRow/listRow";
import { LabeledValue } from "components/designV2/labeledValue/labeledValue";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import SimflexityPayLogo from "images/logo/svg/simflexityPay-logo.svg";
import { PaymentMethod } from "model/Gig";
import { SalaryNotice } from "model/Salary";
import { useTranslation } from "react-i18next";
import { formatDuration } from "utils/utils";
import {
  ActionButtons,
  IconButton,
} from "web-apps/company/components/timeReportsV2/components/views/person/components/row/personSummaryRow.styled";
import styled from "styled-components";

export interface MonthlySalaryRowProps {
  salary: SalaryNotice;
  avatarUrl?: string;
  canAdjustForDisplayedMonth: boolean;
  currency: string;
  onOpenAdjust: (salary: SalaryNotice) => void;
  onOpenPdf: (salary: SalaryNotice) => void;
}

export const MonthlySalaryRow = ({
  salary,
  avatarUrl,
  canAdjustForDisplayedMonth,
  currency,
  onOpenAdjust,
  onOpenPdf,
}: MonthlySalaryRowProps): JSX.Element => {
  const { t } = useTranslation();
  const isSimflexityPay = salary.payrollType === PaymentMethod.SIMFLEXITY_PAY;
  const isLockedByDate = !canAdjustForDisplayedMonth;
  const isAdjustDisabled = isSimflexityPay || isLockedByDate;
  const adjustTooltip = isSimflexityPay
    ? t("Salary.AdjustSalaryDisabledTooltip")
    : isLockedByDate
      ? t("Salary.AdjustSalaryLockedTooltip")
      : t("Salary.AdjustSalaryTooltip");

  return (
    <ListRow>
      <RowCard>
        <RowGrid>
          <RowTop>
            <AvatarChip
              title={salary.workerName}
              subTitle={t("General.Giggers")}
              avatarUrl={avatarUrl}
              titleIcon={
                isSimflexityPay ? (
                  <InlinePayLogo
                    src={SimflexityPayLogo}
                    alt="SimflexityPay logo"
                  />
                ) : null
              }
            />
            <AvatarAndActions>
              <ActionButtons>
                <TooltipHover text={adjustTooltip}>
                  <IconButton
                    disabled={isAdjustDisabled}
                    onClick={() => onOpenAdjust(salary)}
                  >
                    <CustomIcon
                      name="v2/pen"
                      size="20px"
                      color={isAdjustDisabled ? ColorV2.Blue5 : ColorV2.Blue1}
                    />
                  </IconButton>
                </TooltipHover>

                <TooltipHover text={t("Salary.ViewSalaryNoticeTooltip")}>
                  <IconButton onClick={() => onOpenPdf(salary)}>
                    <CustomIcon
                      name="v2/document"
                      size="20px"
                      color={ColorV2.Blue1}
                    />
                  </IconButton>
                </TooltipHover>
              </ActionButtons>
            </AvatarAndActions>
          </RowTop>

          <RowMetrics>
            <LabeledValue
              value={formatDuration(salary.workedHours.toString())}
              label={t("Salary.TotalWorkTime")}
              variant="small"
            />
            <LabeledValue
              value={`${salary.grossSalary}${currency}`}
              label={t("Salary.GrossSalary")}
              variant="small"
            />
            <LabeledValue
              value={`${salary.netSalary}${currency}`}
              label={t("Salary.NetSalary")}
              variant="small"
            />
            <LabeledValue
              value={`${salary.preliminaryTax}${currency}`}
              label={t("Salary.Tax")}
              variant="small"
            />
            <LabeledValue
              value={`${salary.payrollTax}${currency}`}
              label={t("Salary.PayrollTax")}
              variant="small"
            />
            <LabeledValue
              muted={salary.deductionAmount === 0}
              value={`${salary.deductionAmount}${currency}`}
              label={t("Salary.DeductionAmount")}
              variant="small"
            />
            <LabeledValue
              value={
                isSimflexityPay
                  ? salary.taxPercentage + "%"
                  : salary.taxTable.toString()
              }
              label={
                isSimflexityPay ? t("Salary.TaxProcent") : t("Salary.TaxTable")
              }
              variant="small"
            />
          </RowMetrics>
        </RowGrid>
      </RowCard>
    </ListRow>
  );
};

const RowCard = styled.div`
  width: 100%;
`;

const RowGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(200px, 2fr) repeat(7, minmax(80px, 1fr)) auto;
  column-gap: 10px;
  align-items: center;
  width: 100%;

  @media (max-width: 1024px) {
    display: flex;
    flex-direction: column;
    gap: ${Spacing.sm}px;
  }
`;

const RowTop = styled.div`
  display: contents;

  @media (max-width: 1024px) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }
`;

const AvatarAndActions = styled.div`
  order: 100;
  display: flex;
  align-items: center;

  @media (max-width: 1024px) {
    order: unset;
  }
`;

const RowMetrics = styled.div`
  display: contents;

  @media (max-width: 1024px) {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: ${Spacing.md}px ${Spacing.sm}px;
    width: 100%;
  }
`;

const InlinePayLogo = styled.img`
  width: 40px;
  height: 12px;
  object-fit: contain;
`;
